"use client";

import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLocaleFromPathname, useLocalePath } from "@/hooks/useLocalePath";
import { pathMatchesEnglishRoute } from "@/utils/routes";
import Logo from "../Logo";
import MenuIcon from "../icons/MenuIcon";
import Button from "../library/Button";
import DemoButtonText from "./DemoButtonText";

// Simple CloseIcon component
const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Custom NavLink component
type NavLinkProps = {
  href: string;
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
};

const NavLink: React.FC<NavLinkProps> = ({
  href,
  active,
  children,
  onClick,
  className,
}) => {
  return (
    <Link
      href={href}
      className={clsx(
        "transition-colors",
        "hover:text-gail-primary",
        active ? "text-gail-primary" : "text-gray-800",
        className,
      )}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

// Desktop Navigation component
type DesktopNavigationProps = {
  pathname: string | null;
};

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({ pathname }) => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const lang = getLocaleFromPathname(pathname);

  return (
    <div className="hidden md:flex items-center">
      <div className="flex space-x-8 mr-6">
        <NavLink
          href={lp("/product")}
          active={pathMatchesEnglishRoute(pathname, lang, "/product")}
        >
          {t("header.product")}
        </NavLink>
        <NavLink
          href={lp("/pricing")}
          active={pathMatchesEnglishRoute(pathname, lang, "/pricing")}
        >
          {t("header.pricing")}
        </NavLink>
        <NavLink
          href={lp("/compliance")}
          active={pathMatchesEnglishRoute(pathname, lang, "/compliance")}
        >
          {t("header.compliance")}
        </NavLink>
        <NavLink href={process.env.NEXT_PUBLIC_GAIL_DASHBOARD_URL ?? "#"}>
          {t("header.signIn")}
        </NavLink>
      </div>
      <div className="flex">
        <Button href={lp("/contact-sales")} className="text-sm">
          <DemoButtonText fallback={t("header.requestDemo")} />
        </Button>
      </div>
    </div>
  );
};

// Mobile Menu component
type MobileMenuProps = {
  isOpen: boolean;
  toggleMenu: () => void;
  pathname: string | null;
};

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  toggleMenu,
  pathname,
}) => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const lang = getLocaleFromPathname(pathname);

  return (
    <div
      className={clsx(
        "md:hidden",
        "transition-all duration-300",
        "w-full h-dvh",
        "fixed inset-0",
        "bg-[#F9F3E6]",
        "z-50",
        "flex flex-col",
        "px-2 sm:px-4",
        isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none",
      )}
    >
      <div className="flex justify-between px-2 py-4 pt-[17px]">
        <Link href={lp("/")} className="shrink-0" onClick={() => toggleMenu()}>
          <Logo />
        </Link>
        <button
          onClick={toggleMenu}
          className="text-black transition-all duration-300"
          aria-label="Close menu"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex flex-col justify-between px-2 h-full min-h-0">
        <div className="flex flex-col font-semibold text-[40px]">
          <NavLink
            href={lp("/product")}
            active={pathMatchesEnglishRoute(pathname, lang, "/product")}
            onClick={toggleMenu}
            className="border-black/20 border-b"
          >
            {t("header.product")}
          </NavLink>
          <NavLink
            href={lp("/pricing")}
            active={pathMatchesEnglishRoute(pathname, lang, "/pricing")}
            onClick={toggleMenu}
            className="border-black/20 border-b"
          >
            {t("header.pricing")}
          </NavLink>
          <NavLink
            href={lp("/compliance")}
            active={pathMatchesEnglishRoute(pathname, lang, "/compliance")}
            onClick={toggleMenu}
            className="border-black/20 border-b"
          >
            {t("header.compliance")}
          </NavLink>
          <NavLink
            href={process.env.NEXT_PUBLIC_GAIL_DASHBOARD_URL ?? "#"}
            onClick={toggleMenu}
          >
            {t("header.signIn")}
          </NavLink>
        </div>

        <div className="relative flex-1 my-4 min-h-0 overflow-hidden">
          <Image
            src="/images/mobile_nav.webp"
            alt="Gail Menu Graphic"
            fill
            sizes="100vw"
            className="object-contain"
          />
        </div>

        <div className="flex flex-col gap-2 mb-8">
          <Button
            type="tertiary"
            href={lp("/signup")}
            className="py-3 border border-neutral-400 w-full font-normal text-base"
          >
            {t("header.signUp")}
          </Button>
          <Button
            type="primary"
            href={lp("/contact-sales")}
            className="py-3 border border-black w-full font-normal text-base"
          >
            <DemoButtonText fallback={t("header.requestDemo")} />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Header component
const Header: React.FC = () => {
  const [show, setShow] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const lastScroll = useRef(0);
  const ticking = useRef(false);
  const pathname = usePathname();
  const lp = useLocalePath();

  const isMainPage =
    pathname === "/" || /^\/[a-z]{2}(\/)?$/.test(pathname ?? "");

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const threshold = 16;
    const onScroll = () => {
      if (!ticking.current) {
        globalThis.requestAnimationFrame(() => {
          const y = window.scrollY;
          setIsAtTop(y < threshold);
          if (y < threshold) {
            setShow(true);
            lastScroll.current = y;
            ticking.current = false;
            return;
          }
          if (Math.abs(y - lastScroll.current) < threshold) {
            ticking.current = false;
            return;
          }
          if (y < lastScroll.current) setShow(true);
          else if (y > lastScroll.current) setShow(false);
          lastScroll.current = y;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={clsx(
        "sticky",
        "top-0",
        "z-40",
        "w-full",
        isMainPage && isAtTop ? "bg-white/30 backdrop-blur-md" : "bg-white",
        "font-monument",
        "px-4",
        !isOpen && [
          "transition-all",
          "duration-300",
          "will-change-transform",
          show ? "translate-y-0" : "-translate-y-full",
        ],
      )}
    >
      <nav className={clsx("max-w-8xl", "mx-auto", "z-60", "py-4", "md:px-0")}>
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link href={lp("/")} className="shrink-0">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <DesktopNavigation pathname={pathname} />

          {/* Mobile Navigation Toggle */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="text-black transition-all duration-300"
              aria-label="Toggle menu"
            >
              <MenuIcon />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <MobileMenu
          isOpen={isOpen}
          toggleMenu={toggleMenu}
          pathname={pathname}
        />
      </nav>
    </header>
  );
};

export default Header;
